import React, { useState } from 'react';
import { Box, Button, Stack, Alert, CircularProgress } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { AssignedSiteCard } from './AssignedSiteCard';
import { AddSitesPopover } from './AddSitesPopover';
import { RemoveSiteConfirmPopover } from './RemoveSiteConfirmPopover';
import {
  useUserSiteAssignments,
  useAssignSitesToUser,
  useRemoveUserFromSite,
} from '../hooks/useSiteAssignments';
import { usePermissions } from '@billda/permissions-ui';
import { useSnackbarStore } from '../../../shared/stores/snackbarStore';

interface UserSiteAssignmentsProps {
  userId: string;
  userName: string;
}

export const UserSiteAssignments: React.FC<UserSiteAssignmentsProps> = ({
  userId,
  userName,
}) => {
  const [addAnchorEl, setAddAnchorEl] = useState<HTMLButtonElement | null>(
    null
  );
  const [removeAnchorEl, setRemoveAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [removingSiteId, setRemovingSiteId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const permissions = usePermissions();
  const canManage = permissions.includes('sites.assignments.manage');
  const showSnackbar = useSnackbarStore(s => s.showSnackbar);

  const {
    data: assignments = [],
    isLoading,
    error: loadError,
  } = useUserSiteAssignments(userId);

  const assignMutation = useAssignSitesToUser();

  const removeMutation = useRemoveUserFromSite();

  const handleAddSites = (siteIds: number[]) => {
    // Get current site IDs and merge with new selections
    const currentSiteIds = assignments.map(a => a.siteId);
    const allSiteIds = [...new Set([...currentSiteIds, ...siteIds])];

    assignMutation.mutate(
      { userId, siteIds: allSiteIds },
      {
        onSuccess: () => {
          setAddAnchorEl(null);
          setError(null);
          showSnackbar('Sites assigned successfully');
        },
        onError: error => {
          setError(
            error instanceof Error
              ? error.message
              : 'Failed to assign user to sites'
          );
        },
      }
    );
  };

  const handleRemoveClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    siteId: number
  ) => {
    setRemoveAnchorEl(event.currentTarget);
    setRemovingSiteId(siteId);
  };

  const handleConfirmRemove = () => {
    if (removingSiteId) {
      removeMutation.mutate(
        { userId, siteId: removingSiteId },
        {
          onSuccess: () => {
            setRemovingSiteId(null);
            setRemoveAnchorEl(null);
            setError(null);
          },
          onError: error => {
            setError(
              error instanceof Error
                ? error.message
                : 'Failed to remove user from site'
            );
            setRemovingSiteId(null);
            setRemoveAnchorEl(null);
          },
        }
      );
    }
  };

  const handleCancelRemove = () => {
    setRemoveAnchorEl(null);
    setRemovingSiteId(null);
  };

  const assignedSiteIds = assignments.map(a => a.siteId);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box data-testid="user-site-assignments">
      {(error || loadError) && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error ||
            (loadError instanceof Error
              ? loadError.message
              : String(loadError))}
        </Alert>
      )}

      <Stack spacing={1.5}>
        {assignments.map(assignment => (
          <AssignedSiteCard
            key={assignment.siteId}
            assignment={assignment}
            onRemoveClick={handleRemoveClick}
            isRemoving={
              removingSiteId === assignment.siteId && removeMutation.isPending
            }
            canManage={canManage}
          />
        ))}

        {canManage && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={e => setAddAnchorEl(e.currentTarget)}
            fullWidth
            data-testid="add-sites-button"
          >
            Add Sites
          </Button>
        )}
      </Stack>

      <AddSitesPopover
        anchorEl={addAnchorEl}
        onClose={() => setAddAnchorEl(null)}
        onAddSites={handleAddSites}
        userName={userName}
        assignedSiteIds={assignedSiteIds}
        isSubmitting={assignMutation.isPending}
      />

      <RemoveSiteConfirmPopover
        anchorEl={removeAnchorEl}
        siteName={
          removingSiteId
            ? assignments.find(a => a.siteId === removingSiteId)?.siteName || ''
            : ''
        }
        onClose={handleCancelRemove}
        onConfirm={handleConfirmRemove}
        isRemoving={removeMutation.isPending}
      />
    </Box>
  );
};
